import { useEffect, useRef, useState, useContext } from 'react'
import { pick, __, values, pipe, pathOr, filter, propEq } from 'ramda'
import { Text, Transformer, Label, Tag, Group } from 'react-konva'
import { Html } from 'react-konva-utils'
import styled from 'styled-components'
import { camelCase } from 'lodash'
import { colors } from '@/styles'
import Duplicate from './duplicate.svg?react'
import Trash from './trash.svg?react'
import TextSize from './text-size.svg?react'
import DropdownComponent from '../dropdown-component'
import { Dropdown, DropdownItem } from '../dropdown'
import Context from '../context'
import { getTextWidth } from '../text-width'
import inputTextSizes from '../text-sizes'
import { updateAt } from '../utils'
const getLabelProps = pick(['x', 'y'])
const getTextProps = pick(['width', 'height', 'fontSize'])
const getStrokeProps = pick(['stroke'])

const DeleteWrapper = styled.div``

const HtmlContentWrapper = styled.div`
  width: ${props => (props.isText ? '175px' : '304px')};
  height: 40px;
  background-color: ${colors.grey32};
  border-radius: 8px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.isText ? 'space-between' : 'space-between')};
`

const HtmlContentInner = styled.div`
  flex: 0 0 228px;
  width: 228px;

  ${props =>
    props.isText &&
    `
    display: flex;
    gap: 10px;
    flex: 0 0 100px;
    width: 100px;
 
  `}
`

const TextSizeWidgetWrapper = styled.div`
  display: flex;
`

const DuplicateWrapper = styled.div``

const RemoveIcon = ({ handleDelete }) => {
  return (
    <DeleteWrapper onClick={handleDelete}>
      <Trash fill="#ffffff" style={{ marginTop: '4px' }} alt="trash" />
    </DeleteWrapper>
  )
}

const DuplicateIcon = ({ handleDuplicate }) => {
  return (
    <DuplicateWrapper onClick={handleDuplicate}>
      <Duplicate fill="#ffffff" style={{ marginTop: '4px' }} alt="duplicate" />
    </DuplicateWrapper>
  )
}

const TextSizeWidget = ({ inputTextSize }) => {
  return (
    <TextSizeWidgetWrapper>
      <TextSize fill="#000000" style={{ marginRight: '6px' }} alt="" /> {inputTextSize}
    </TextSizeWidgetWrapper>
  )
}

const InputWidget = ({
  shapeProps,
  isSelected,
  onSelect,
  onChange,
  text,
  parentRef,
  id,
  name,
  currentItems,
  selectShape,
  handleDuplicate,
  handleDelete,
  currentStep,
  isSigning,
  isMobile,
  inputType,
  currentDocument,
  contractId,
  signees,
  setSignees
}) => {
  const htmlRef = useRef()
  const labelRef = useRef()
  const textRef = useRef()
  const tagRef = useRef()
  const transformerRef = useRef()
  const textAreaRef = useRef()
  const userContext = useContext(Context)

  const { items, setItems, fileMetadata, setFileMetadata, user, setUser } = userContext

  const currentSignees = pipe(
    pathOr({}, [currentDocument]),
    values,
    filter(propEq('recipient_signing_status', 'signature_required'))
  )(signees)
  const currentItem = currentItems.filter(obj => obj.id === id)[0]
  const [inputTextValue, setInputTextValue] = useState('')
  const [inputTextVisible, setInputTextVisible] = useState(false)
  const [inputTextHeight, setInputTextHeight] = useState(shapeProps.height + 8) // to account for padding
  const [inputTextSize, setInputTextSize] = useState(shapeProps.textSize)
  const [allowResize, setAllowResize] = useState(true)
  const textType = inputType === 'text'
  const initialType = inputType === 'initial'
  const blackHex = '#000000'
  const extraWidthMargin = 10
  const initialTypeWidth = 50

  useEffect(() => {
    setInputTextValue(currentItem?.text)
    setInputTextSize(currentItem?.textSize)
  }, [currentItem])

  useEffect(() => {
    const newItems = currentItems.map(item => {
      if (item.id === currentItem.id) {
        return {
          ...item,
          textSize: parseInt(inputTextSize),
          height:
            inputTextHeight > inputTextSize ? parseInt(inputTextHeight) : parseInt(inputTextSize)
        }
      }
      return item
    })
    const updatedItems = updateAt(items, [currentDocument], newItems)
    setItems(updatedItems)
  }, [inputTextSize])

  useEffect(() => {
    document.dispatchEvent(new Event('drop'))
  }, [items])

  useEffect(() => {
    if (isSelected) {
      // we need to attach transformer manually
      transformerRef.current.nodes([labelRef.current])
      transformerRef.current.getLayer().batchDraw()
    }
  }, [isSelected])

  const handleBlurTextChange = () => {
    setInputTextVisible(false)
    const stageWidth = parentRef.current.width() - 20
    const inputTextWidth = getTextWidth(inputTextValue, `bold ${inputTextSize}px arial`)
    const newItems = currentItems.map(item => {
      const [userId, __, randomId] = item.id.split('_')
      const newId = `${userId}_${camelCase(inputTextValue)}_${randomId}`
      if (item.id === currentItem.id) {
        return {
          ...item,
          id: newId,
          text: inputTextValue,
          ...(allowResize && {
            width: inputTextWidth > stageWidth ? parseInt(stageWidth) : parseInt(inputTextWidth)
          })
        }
      }
      return item
    })

    setItems(updateAt(items, [currentDocument], newItems))
  }

  const handleDoubleClick = () => {
    setInputTextVisible(true)
  }

  const handleInputTextChange = e => {
    setInputTextValue(e.target.value)
  }

  const handleInputTextKeyDown = e => {
    if (e.keyCode === 13) {
      setInputTextHeight(parseInt(e.target.scrollHeight))
    }
  }

  const handleDropdown = e => {
    if (isSigning) return
    const currentDropdown = parentRef.current.children[0].find(`#${id}`)
    const currentName = currentDropdown[0].attrs.name
    let textAttrs

    currentDropdown[0].find('Label').forEach(item => {
      textAttrs = item?.children[2]?.attrs
    })

    const newId = `${e.props.id}_${camelCase(textAttrs?.text)}_${Math.floor(Math.random() * 10000)}`
    const newItems = currentItems.map(item => {
      if (item.id === currentItem.id) {
        return {
          ...item,
          id: newId,
          name: currentName,
          stroke: e.props.color,
          recipient_id: e.props?.recipient_id
        }
      }
      return item
    })
    setItems(updateAt(items, [currentDocument], newItems))
    selectShape(newId)
    parentRef.current.draw()
  }

  return (
    <Group id={id} name={name} onDblClick={handleDoubleClick}>
      <Label
        {...getLabelProps(shapeProps)}
        id={`${id}-label`}
        name={`${name}`}
        {...(isMobile ? { onClick: onSelect } : { onTap: onSelect })}
        draggable={
          currentStep === 'createContract' || currentStep === 'upload' || isSigning ? false : true
        }
        ref={labelRef}
        color={getStrokeProps(shapeProps).stroke}
        onDragEnd={e => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y()
          })
        }}
        onClick={e => {
          const color =
            e.target.attrs.type === 'text' ? e.target.attrs.typeColor : e.target.attrs.fill
          for (const [key, value] of Object.entries(currentSignees)) {
            if (color === value.color) {
              const dropdownItem = (
                <DropdownComponent
                  color={value.color}
                  name={`${value.first_name} ${value.last_name}`}
                  email={value.email}
                  id={value?.recipient_id}
                  recipient_id={value?.recipient_id}
                  items={items}
                />
              )
              userContext.setUser(dropdownItem)
              handleDropdown(dropdownItem)
            }
          }
        }}
        onDragMove={e => {
          const target = e?.target
          // Maybe be a better way to get stage
          const stage = target.parent.parent.parent
          const width = stage.width()
          const height = stage.height()
          const sx = stage.scaleX()
          const sy = stage.scaleY()
          const tx = target.width() * sx
          const ty = target.height() * sy
          const x0 = target.x() * sx
          const y0 = target.y() * sy
          const x1 = x0 + tx
          const y1 = y0 + ty
          // Prevent widget from going off document
          if (x0 < 0) target.setX(0)
          if (y0 < 0) target.setY(0)
          // We need to set position without stage scale
          if (x1 > width) target.setX((width - tx) / sx)
          if (y1 > height) target.setY((height - ty) / sy)
        }}
        onTransformStart={() => {
          const node = labelRef.current
          // Node children 1 is Tag
          // node.children[1].attrs.fill = node.children[0].attrs.stroke
          // Node children 2 is Text
          node.children[2].attrs.fill = node.children[0].attrs.stroke
        }}
        onTransformEnd={() => {
          const node = labelRef.current
          const scaleX = node.scaleX()
          const scaleY = node.scaleY()

          // node.children[1].attrs.fill = ''
          node.children[2].attrs.fill = textType ? blackHex : userContext.user.props.color

          node.scaleX(1)
          node.scaleY(1)
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            width: Math.max(5, node.width() * scaleX),
            height: Math.max(node.height() * scaleY)
          })
          labelRef.current._sync()
          labelRef.current.getLayer().draw()
          transformerRef.current.forceUpdate()
          // Set the text field height
          setInputTextHeight(parseInt(node.height()))
          setAllowResize(false)
        }}
      >
        {isSelected && !isSigning && (
          <Html
            transform={true} // should we apply position transform automatically to DOM container, default is true
            transformFunc={transformAttrs => ({ ...transformAttrs, scaleX: 1, scaleY: 1 })} // function to overwrite transformation attributes, not used if transform = false, default is undefined
            groupProps={{}} // additional properties to the group wrapper, useful for some position offset
            divProps={{ style: { marginTop: '-45px', position: 'absolute' } }} // additional props for wrapped div elements, useful for styles
          >
            <HtmlContentWrapper ref={htmlRef} isText={textType}>
              <HtmlContentInner isText={textType}>
                {!textType && (
                  <Dropdown
                    value="User Name"
                    display={
                      <DropdownComponent
                        color={userContext.user.props.color}
                        name={`${userContext.user.props.name}`}
                        id={userContext.user.props.id}
                        recipient_id={userContext.user.props.recipient_id}
                      />
                    }
                    height="32"
                    onChange={e => {
                      userContext.setUser(e)
                      handleDropdown(e)
                    }}
                    fileMetadata={fileMetadata}
                    contractId={contractId}
                    signees={signees}
                    setSignees={setSignees}
                    setFileMetadata={setFileMetadata}
                    currentUser={user}
                    setUser={setUser}
                    items={items}
                    setItems={setItems}
                  >
                    {Object.keys(currentSignees).map((key, i) => {
                      return (
                        <DropdownItem
                          key={`currentSignees-dropdown-${key}`}
                          value={
                            <DropdownComponent
                              color={currentSignees[key].color}
                              name={`${currentSignees[key].first_name} ${currentSignees[key].last_name}`}
                              email={currentSignees[key].email}
                              id={key}
                              recipient_id={currentSignees[key]?.recipient_id}
                            />
                          }
                        >
                          <DropdownComponent
                            color={currentSignees[key].color}
                            name={`${currentSignees[key].first_name} ${currentSignees[key].last_name}`}
                            email={currentSignees[key].email}
                            id={key}
                            recipient_id={currentSignees[key]?.recipient_id}
                          />
                        </DropdownItem>
                      )
                    })}
                  </Dropdown>
                )}
                {textType && (
                  <Dropdown
                    display={<TextSizeWidget inputTextSize={inputTextSize} />}
                    isText={textType}
                    onChange={e => {
                      setInputTextSize(parseInt(e))
                    }}
                  >
                    {inputTextSizes.map((item, i) => {
                      return (
                        <DropdownItem key={i} value={item}>
                          {item}
                        </DropdownItem>
                      )
                    })}
                  </Dropdown>
                )}
              </HtmlContentInner>
              <DuplicateIcon handleDuplicate={handleDuplicate} />
              <RemoveIcon handleDelete={handleDelete} labelRef={labelRef} />
            </HtmlContentWrapper>
          </Html>
        )}
        <Tag
          fill={textType ? 'rgb(255, 255, 255, 0.5)' : shapeProps.stroke}
          opacity={!isSigning && textType ? 1 : 0.1}
          stroke={!isSigning && textType ? shapeProps.stroke : null}
          strokeWidth={!isSigning && textType ? 2 : null}
          ref={tagRef}
        />
        {textType && inputTextVisible && (
          <Html
            transform={true}
            groupProps={{}}
            divProps={{
              style: {
                width: `${getTextProps(shapeProps).width + extraWidthMargin}px`,
                height: `${getTextProps(shapeProps).height}px`,
                zIndex: 1
              }
            }}
          >
            <textarea
              ref={textAreaRef}
              value={inputTextValue}
              onBlur={handleBlurTextChange}
              onChange={handleInputTextChange}
              onKeyDown={handleInputTextKeyDown}
              maxLength="4000"
              style={{ width: '100%', height: '100%', textAlign: 'left', resize: 'none' }}
            />
          </Html>
        )}
        <Text
          fill={textType ? blackHex : shapeProps.stroke}
          ref={textRef}
          text={textType ? currentItem.text : text}
          type="text"
          typeColor={shapeProps.stroke}
          ellipsis
          fontSize={textType ? inputTextSize : 16}
          align={textType ? 'left' : 'center'}
          verticalAlign={textType ? 'top' : 'middle'}
          onClick={onSelect}
          onTap={onSelect}
          wrap="word"
          padding={4}
          width={initialType ? initialTypeWidth : shapeProps.width + extraWidthMargin}
          height={shapeProps.height}
          onMouseEnter={e => {
            const container = e.target.getStage().container()
            container.style.cursor =
              isSigning || currentStep === 'createContract' ? 'pointer' : 'move'
          }}
          onMouseLeave={e => {
            const container = e.target.getStage().container()
            container.style.cursor = 'pointer'
          }}
        />
      </Label>
      {isSelected && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            return newBox
          }}
        />
      )}
    </Group>
  )
}

export default InputWidget
